import {
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialogButtons,
  AlertDialogDescription,
  AlertDialogTitle
} from "@/constants/alert-dialog/alert-dialog"
import {
  changeWorkflowStatus,
  createWorkflow,
  deleteWorkflow,
  getSingleWorkflow,
  runWorkflow,
  publishDraftWorkflow
} from "@/services/workflowService"
import { WorkflowsTableRow } from "@/types/workflow"
import { useNavigate } from "react-router-dom"
import { ColumnMetaData, TableDataProps } from "@/types/types"
import { TableHeaderComponent } from "@/components/custom/Table/TableHeader"
import { EmptyTableRow } from "@/components/custom/Table/EmptyTableRow"
import { ButtonVariant } from "@/constants/button/button"
import { StatusToggle } from "@/components/custom/Table/Status"
import { WorkflowStatus } from "@/constants/workflows/workflows"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { UserRole } from "@/constants/users/userRole"
import { WorkflowRunning } from "@/components/custom/AlertDialogButton/WorkflowRunning"
import { CustomTableCell } from "@/components/custom/Table/TableCell"
import { TableCellType } from "@/constants/table/table"
import { Play, Trash2, Copy, Check } from "lucide-react";
import { useState } from "react"
import { ConfirmationModal } from "@/components/custom/AlertDialogButton/ConfirmationModal"
import { ActionMenu } from "@/components/custom/Table/TableActionMenu"
import { buildDuplicateWorkflowRequestBody } from "./CreateWorkflow/helpers/buildDuplicateWorkflowRequestBody"
import { Badge } from "@/components/ui/badge"


export const DataTable = ({
  columns,
  data,
}: TableDataProps<WorkflowsTableRow>
) => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const [openRunDialog, setOpenRunDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false)
  const [openPublishDialog, setOpenPublishDialog] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowsTableRow | null>(null)

  const handleRunClick = (workflow: WorkflowsTableRow) => {
    setSelectedWorkflow(workflow)
    setOpenRunDialog(true)
  }

  const handleDeleteClick = (workflow: WorkflowsTableRow) => {
    setSelectedWorkflow(workflow)
    setOpenDeleteDialog(true)
  }

  const handleDuplicateClick = (workflow: WorkflowsTableRow) => {
    setSelectedWorkflow(workflow)
    setOpenDuplicateDialog(true)
  }

  const handlePublishDraftClick = (workflow: WorkflowsTableRow) => {
    setSelectedWorkflow(workflow)
    setOpenPublishDialog(true)
  }

  const toggleWorkflowStatus = async (workflowId: string) => {
    if (!token) return
    await dispatch(changeWorkflowStatus(token, workflowId))
  }

  const deleteWorkflowById = async (workflowId: string) => {
    if (!token) return
    await dispatch(deleteWorkflow(token, workflowId))
  }

  const runWorkflowById = async (workflowId: string) => {
    if (!token) return
    await dispatch(runWorkflow(token, workflowId))
  }

  const publishDraftWorkflowById = async (workflowId: string) => {
    if (!token) return
    await dispatch(publishDraftWorkflow(token, workflowId))
  }

  const duplicateWorkflowById = async (workflowId: string) => {
    try {
      if (!token) return
      const workflowDetailsResp = await dispatch(getSingleWorkflow(token, workflowId))
      if (!workflowDetailsResp?.data?.workflow) return
      const newRequestBody = buildDuplicateWorkflowRequestBody(workflowDetailsResp?.data?.workflow)
      const createdWorkflowResp = await dispatch(createWorkflow(token, newRequestBody, false))
      // Always set duplicated workflow to inactive
      // This is to avoid accidental runs of duplicated workflows
      if (!createdWorkflowResp.data?.workflow.id) return
      await dispatch(changeWorkflowStatus(token, createdWorkflowResp.data?.workflow.id))
    } catch (error) {
      alert("Failed to duplicate workflow. Please try again.")
    }
  }

  return (
    <div className="rounded-md border w-full">
      <Table>
        <TableHeaderComponent data={data} columns={columns} />
        <TableBody className="text-primary">
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              (() => {
                const workflow = row.original as WorkflowsTableRow
                const isActive = workflow.status === WorkflowStatus.ACTIVE;
                const isDraft = workflow.status === WorkflowStatus.PENDING

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={`bg-primary-foreground hover:bg-border/5 hover:cursor-auto ${isDraft ? "bg-background text-secondary" : ""}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const workflow = cell.getContext().row.original;

                      if (cell.column.id === 'actions') {
                        const menuItems = [
                          { label: 'Publish Workflow', onClick: () => handlePublishDraftClick(workflow), hidden: !isDraft, disabled: false, icon: <Check className="text-accent" size={16} /> },
                          { label: "Run Workflow", onClick: () => handleRunClick(workflow), disabled: !isActive || isDraft, icon: <Play className="text-accent" size={16} /> },
                          { label: "Duplicate", onClick: () => handleDuplicateClick(workflow), disabled: isDraft, icon: <Copy className="text-accent" size={16} /> },
                          { label: "Delete Workflow", onClick: () => handleDeleteClick(workflow), disabled: isActive, divider: true, icon: <Trash2 className="text-destructive" size={16} /> },
                        ];

                        return (
                          <TableCell key={cell.id} className="w-20 text-right">
                            {userData?.role !== UserRole.USER && <ActionMenu items={menuItems} menuType="horizontal" />}
                          </TableCell>
                        );
                      } else if (cell.column.id === 'status') {
                        return (
                          <TableCell key={cell.id} className="w-60">
                            {cell.getContext().row.original.status === WorkflowStatus.PROCESSING ? (
                              <WorkflowRunning
                                actionButtonText={AlertDialogButtons.PROCESSING_WORKFLOW}
                                dialogTitle={`${cell.getContext().row.original.nickname}
                                ${AlertDialogTitle.PROCESSING_WORKFLOW}`}
                                dialogSubtitle={AlertDialogDescription.PROCESSING_WORKFLOW}
                                variant={ButtonVariant.DEFAULT}
                              />
                            ) : cell.getContext().row.original.status === WorkflowStatus.PENDING ? (
                              <Badge variant="outline" className="text-secondary border-secondary cursor-pointer">
                                Draft
                              </Badge>
                            ) : (
                              <StatusToggle
                                status={cell.getContext().row.original.status}
                                onChange={() => toggleWorkflowStatus(cell.getContext().row.original.id)}
                              />
                            )}
                          </TableCell>
                        )
                      } else {
                        return (
                          <CustomTableCell
                            key={cell.id}
                            cell={cell}
                            additionalClassName={isDraft ? "opacity-70" : ""}
                            onClick={() => {
                              if ((cell.column.columnDef.meta as ColumnMetaData)?.type === TableCellType.LINK_BUTTON) {
                                navigate(`/workflows/edit/${cell.getContext().row.original.id}`)
                              }
                            }}
                          />
                        )
                      }
                    })}
                  </TableRow>
                )
              })()
            ))
          ) : (
            <EmptyTableRow numberOfColumns={columns.length} />
          )}
        </TableBody>
      </Table>
      {/* Run Confirmation */}
      <ConfirmationModal
        open={openRunDialog}
        onOpenChange={setOpenRunDialog}
        actionButtonText={AlertDialogButtons.RUN_WORKFLOW}
        dialogTitle={AlertDialogTitle.RUN_WORKFLOW}
        dialogSubtitle={AlertDialogDescription.RUN_WORKFLOW}
        variant={ButtonVariant.DEFAULT}
        onConfirm={() => {
          if (selectedWorkflow) runWorkflowById(selectedWorkflow.id)
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
        actionButtonText={AlertDialogButtons.DELETE}
        dialogTitle={`${AlertDialogTitle.DELETE_WORKFLOW} ${selectedWorkflow?.nickname}?`}
        dialogSubtitle={AlertDialogDescription.DELETE_WORKFLOW}
        variant={ButtonVariant.DESTRUCTIVE}
        onConfirm={() => {
          if (selectedWorkflow) deleteWorkflowById(selectedWorkflow.id)
        }}
      />

      {/* Duplicate Confirmation */}
      <ConfirmationModal
        open={openDuplicateDialog}
        onOpenChange={setOpenDuplicateDialog}
        actionButtonText={AlertDialogButtons.DUPLICATE}
        dialogTitle={`${AlertDialogTitle.DUPLICATE}`}
        dialogSubtitle={`${AlertDialogDescription.DUPLICATE} - ${selectedWorkflow?.nickname}`}
        variant={ButtonVariant.DEFAULT}
        onConfirm={() => {
          if (selectedWorkflow) duplicateWorkflowById(selectedWorkflow.id)
        }}
      />

      {/* Publish Draft Confirmation */}
      <ConfirmationModal
        open={openPublishDialog}
        onOpenChange={setOpenPublishDialog}
        actionButtonText={AlertDialogButtons.PUBLISH_DRAFT}
        dialogTitle={`${AlertDialogTitle.PUBLISH_DRAFT} ${selectedWorkflow?.nickname}?`}
        dialogSubtitle={`${AlertDialogDescription.PUBLISH_DRAFT}`}
        variant={ButtonVariant.DEFAULT}
        onConfirm={() => {
          if (selectedWorkflow) publishDraftWorkflowById(selectedWorkflow.id)
        }}
      />
    </div>
  )
}
